import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { Add, TableChart, Visibility } from '@mui/icons-material';
import { AlertType, useAlert } from 'src/components/AlertsProvider';
import ApplicationCard from 'src/pages/Solutions/SolutionAssetHierarchy/solutionAssetOmniverseApp/ApplicationCard';
import { useStoreUnitActions } from 'src/redux/helpers';
import { PageCardLoader } from 'src/components/PageCardLayout';
import SolutionOmniverseCreateContainer from './SolutionOmniverseCreateContainer';
import SolutionOmniverseDeleteModal from './SolutionOmniverseDeleteModal';
import SolutionOmniverseSessionsModal from './SolutionOmniverseSessionsModal';
import solutionsStore, {
  useOmniverseApp,
  useOmniverseSessions,
} from '../../redux';
import { AppVersion, SolutionOmniverseApp } from '../../types';
import SolutionOmniverseEditContainer from './SolutionOmniverseEditContainer';

const OmniverseApplication = ({ siteId, twinId, levelId }) => {
  const [appData, appLoading] = useOmniverseApp();
  const [sessionsData, sessionsLoading] = useOmniverseSessions();
  const [lastUpdate, setLastUpdate] = useState<string>('');
  const [openEditForm, setOpenEditForm] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openSessionsModal, setOpenSessionsModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState<SolutionOmniverseApp | null>(
    null
  );
  const [openCreateForm, setOpenCreateForm] = useState(false);
  const [sessionFilters, setSessionFilters] = useState<{
    status?: number;
  }>({});
  const [sessionPage, setSessionPage] = useState(1);
  const [sessionPageSize, setSessionPageSize] = useState(10);
  const { showAlert } = useAlert();
  const actions = useStoreUnitActions(solutionsStore);
  const { solutionId } = useParams();

  const handleCreateNew = () => {
    setSelectedApp({
      id: '',
      solutionId: '',
      siteId: '',
      functionId: '',
      functionVersionId: '',
      title: '',
      description: '',
      version: '',
      image: '',
      icon: '',
      category: '',
      productArea: '',
      authenticationType: '',
      mediaServer: '',
      mediaPort: 0,
      status: '',
      createdBy: '',
      createdAt: '',
      updatedBy: '',
      updatedAt: '',
    });
    setOpenCreateForm(true);
  };

  const handleViewSessions = () => {
    // Reset filters and pagination
    setSessionPage(1);
    setSessionPageSize(10);
    setSessionFilters({});

    // Fetch sessions data with initial params
    actions.omniverseSessions({
      solutionId,
      siteId,
      twinId,
      page: 1,
      pageSize: 10,
    });
    setOpenSessionsModal(true);
  };

  const handleSessionPageChange = (page: number, pageSize: number) => {
    setSessionPage(page);
    setSessionPageSize(pageSize);

    actions.omniverseSessions({
      solutionId,
      siteId,
      twinId,
      page,
      pageSize,
      ...sessionFilters,
    });
  };

  const handleSessionFilterChange = (filters: { status?: number }) => {
    setSessionFilters(filters);
    setSessionPage(1); // Reset to first page when filters change

    actions.omniverseSessions({
      solutionId,
      siteId,
      twinId,
      page: 1,
      pageSize: sessionPageSize,
      ...filters,
    });
  };

  const [categories, setCategories] = useState<
    Map<string, Map<string, SolutionOmniverseApp>>
  >(new Map());

  const handleEdit = (app: SolutionOmniverseApp) => {
    setSelectedApp({ ...app });
    setOpenEditForm(true);
  };

  const handleDelete = (app: SolutionOmniverseApp) => {
    setSelectedApp(app);
    setOpenDeleteDialog(true);
  };

  const organizeAppsByCategory = (apps: SolutionOmniverseApp[]) => {
    const newCategories = new Map<string, Map<string, SolutionOmniverseApp>>();

    apps.forEach((app) => {
      const category = app.category || 'Other';
      if (!newCategories.has(category)) {
        newCategories.set(category, new Map<string, SolutionOmniverseApp>());
      }

      const categoryMap = newCategories.get(category)!;

      const functionId = app.functionId || '';
      const functionVersionId = app.functionVersionId || '';
      const appKey = `${app.title}-${functionId}`;

      const existingApp = categoryMap.get(appKey);

      if (!existingApp) {
        const currentVersion: AppVersion = {
          id: app.id,
          name: app.version,
          functionId: app.functionId,
          functionVersionId: app.functionVersionId,
        };

        const appWithVersions = {
          ...app,
          versions: [currentVersion],
          latestVersion: currentVersion,
        };
        categoryMap.set(appKey, appWithVersions);
      } else {
        const newVersion: AppVersion = {
          id: app.id,
          name: app.version,
          functionId: app.functionId,
          functionVersionId: app.functionVersionId,
        };

        if (
          existingApp.versions &&
          !existingApp.versions.some((v) => v.id === app.id)
        ) {
          existingApp.versions.push(newVersion);
        } else if (!existingApp.versions) {
          existingApp.versions = [newVersion];
        }
        if (
          !existingApp.latestVersion ||
          app.version > existingApp.latestVersion.name
        ) {
          existingApp.latestVersion = newVersion;

          Object.assign(existingApp, {
            id: app.id,
            title: app.title,
            description: app.description,
            image: app.image,
            icon: app.icon,
            productArea: app.productArea,
            status: app.status,
            mediaServer: app.mediaServer,
            mediaPort: app.mediaPort,
            updatedAt: app.updatedAt,
          });
        }
      }
    });

    Array.from(newCategories.values()).forEach((categoryMap) => {
      Array.from(categoryMap.values()).forEach((app) => {
        if (app.versions && app.versions.length > 0) {
          app.versions.sort((a, b) => {
            if (a.name > b.name) return -1;
            if (a.name < b.name) return 1;
            return 0;
          });
        }
      });
    });

    return newCategories;
  };

  useEffect(() => {
    if (appData && appData.length > 0) {
      const newCategories = organizeAppsByCategory([...appData]);
      setCategories(newCategories);
    } else {
      setCategories(new Map());
    }
  }, [JSON.stringify(appData)]);

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#fff',
    padding: 0,
  };

  return (
    <Box sx={containerStyle}>
      <Stack spacing={2}>
        <Box
          sx={{
            // mb: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<Visibility />}
            onClick={handleViewSessions}
            color="primary"
            size="small"
            disabled={levelId === 0 || twinId === undefined}
          >
            View all sessions
          </Button>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={handleCreateNew}
            color="primary"
            size="small"
            disabled={levelId === 0 || twinId === undefined}
          >
            Add Application
          </Button>
        </Box>

        {!appLoading && categories.size === 0 && (
          <Typography variant="body2" color="#A5A5A5" align="center">
            No data to display
          </Typography>
        )}

        {!appLoading &&
          categories.size > 0 &&
          Array.from(categories.entries()).map(([category, apps]) => (
            <Card key={category} variant="outlined" sx={{ borderRadius: 0 }}>
              <CardContent sx={{ minHeight: '0px' }}>
                <Stack spacing={2}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      // pb: 1,
                      borderBottom: '2px solid gray',
                    }}
                  >
                    <TableChart fontSize="small" />
                    <Typography fontSize="20px">{category}</Typography>
                  </Box>

                  <Grid container spacing={2}>
                    {Array.from(apps.values()).map((app) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        sx={{ paddingRight: 3 }}
                        // md={6}
                        // lg={6}
                        // xl={6}
                        key={`${app.id}-${app.version}-${app.updatedAt}`}
                      >
                        <ApplicationCard
                          app={{
                            id: app.id,
                            title: app.title,
                            icon: app.image || '',
                            productArea: app.productArea,
                            category: app.category,
                            description: app.description,
                            status: app.status as
                              | 'active'
                              | 'inactive'
                              | 'maintenance',
                            lastUpdated: app.updatedAt,
                            latestVersion: app.latestVersion
                              ? {
                                  id: app.latestVersion.id,
                                  name: app.latestVersion.name,
                                }
                              : {
                                  id: app.id,
                                  name: app.version,
                                },
                            versions:
                              app.versions && app.versions.length > 0
                                ? app.versions.map((v) => ({
                                    id: v.id,
                                    name: v.name,
                                  }))
                                : [{ id: app.id, name: app.version }],
                          }}
                          onEdit={() => handleEdit(app)}
                          onDelete={() => handleDelete(app)}
                          solutionId={solutionId}
                          siteId={siteId}
                          twinId={twinId}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
              </CardContent>
            </Card>
          ))}
        {selectedApp && openCreateForm && (
          <SolutionOmniverseCreateContainer
            item={selectedApp}
            sitesId={siteId}
            id={twinId}
            open={openCreateForm}
            onClose={(_, reason) => {
              setOpenCreateForm(false);
            }}
          />
        )}
      </Stack>

      <SolutionOmniverseDeleteModal
        open={openDeleteDialog}
        app={selectedApp}
        sitesId={siteId}
        id={twinId}
        onClose={() => setOpenDeleteDialog(false)}
        onSuccess={() =>
          actions.omniverseApp({
            solutionId,
            siteId,
            twinId,
            functionId: '',
            functionVersionId: '',
          })
        } // Refresh data
      />

      {selectedApp && (
        <SolutionOmniverseEditContainer
          item={selectedApp}
          sitesId={siteId}
          id={twinId}
          open={openEditForm}
          onClose={(event, reason) => {
            setOpenEditForm(false);
            if (reason !== 'completeAction') {
              setSelectedApp(null);
            }
          }}
        />
      )}

      <SolutionOmniverseSessionsModal
        open={openSessionsModal}
        onClose={() => setOpenSessionsModal(false)}
        sessions={sessionsData}
        loading={sessionsLoading || false}
        totalItems={sessionsData?.length || 0}
        onPageChange={handleSessionPageChange}
        onFilterChange={handleSessionFilterChange}
        currentPage={sessionPage}
        currentPageSize={sessionPageSize}
        twinId={twinId}
      />

      {appLoading && <PageCardLoader />}
    </Box>
  );
};

export default OmniverseApplication;
